import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import {
  ArraySortPipe,
  ControlErrorDirective,
  LabelValueComponent,
  UIBlockerService
} from '@howdeniberia/core-front';
import { forkJoin, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, finalize, map, switchMap, takeUntil } from 'rxjs/operators';
import { IResponseMessage } from 'src/app/core/models/api/sane-project/arpa-model';
import { CalculatorProjectService } from 'src/app/core/services/api/calculator/calculator-project.service';
import { BonusPipe } from 'src/app/core/services/api/shared/bonus.pipe';
import { SharedService } from 'src/app/core/services/api/shared/shared.service';
import { normalizeTraceHtml, toNumber } from 'src/app/shared/utils';
import { ProjectViewModel } from '../../models/project.viewmodel';
import { CalculatorMapper } from '../mappers/calculator.mapper';
import { CalculatorProductViewModel, dependencePresets } from '../models/calculator-product.viewmodel';
import { NumberMaxDirective } from '../models/number-max.directive';

@Component({
  selector: 'howden-calculator-dependence',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatButtonModule,
    MatDividerModule,
    ArraySortPipe,
    ControlErrorDirective,
    LabelValueComponent,
    BonusPipe,
    NumberMaxDirective
  ],
  templateUrl: './calculator-dependence.component.html',
  styleUrls: ['./calculator-dependence.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CalculatorDependenceComponent implements OnDestroy {
  private readonly fb = inject(FormBuilder);
  private readonly uiBlocker = inject(UIBlockerService);
  private readonly calcSrv = inject(CalculatorProjectService);
  private readonly sharedSrv = inject(SharedService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly sanitizer = inject(DomSanitizer);

  form: FormGroup;

  readonly dependenceAxa = new CalculatorProductViewModel(dependencePresets['axa']);
  readonly dependenceSurne = new CalculatorProductViewModel(dependencePresets['surne']);

  project!: ProjectViewModel;
  private readonly destroy$ = new Subject<void>();

  compareByIdNumber = this.calcSrv.compareByIdNumber.bind(this.calcSrv);
  canSeeTrace = false;

  private readonly vmMap = {
    axa: this.dependenceAxa,
    surne: this.dependenceSurne
  } as const;

  constructor() {
    const sharedSrv = this.sharedSrv;

    this.form = this.fb.group({
      dependenceAxa: this.dependenceAxa.form,
      dependenceSurne: this.dependenceSurne.form
    });
    this.canSeeTrace = sharedSrv.getCanSeeTrace();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public shouldShowZeroState(vm: CalculatorProductViewModel): boolean {
    return !vm.hasAnyPositiveNumeric();
  }

  public showTrace(): boolean {
    return !!this.canSeeTrace;
  }

  public getTraceHtml(vm: CalculatorProductViewModel): SafeHtml | '' {
    if (!this.showTrace() || !vm.trace) { return ''; }
    const normalized = normalizeTraceHtml(vm.trace);
    return this.sanitizer.bypassSecurityTrustHtml(normalized);
  }

  public getMessages(vm: CalculatorProductViewModel): IResponseMessage[] {
    return vm.messages ?? [];
  }

  private setAxaGreatFromSevere(): void {
    const sevCtrl = this.dependenceAxa.form.get('depAxaSevereDependency');
    const greatCtrl = this.dependenceAxa.form.get('depAxaGreatDependency');
    if (!sevCtrl || !greatCtrl) { return; }
    const sev = toNumber(sevCtrl.value) ?? 0;
    const great = sev * 2;
    if (toNumber(greatCtrl.value) !== great) {
      greatCtrl.patchValue(great, { emitEvent: false });
    }
  }

  private setSurneGreatFromSevere(): void {
    const sevCtrl = this.dependenceSurne.form.get('depSurneSevereDependency');
    const greatCtrl = this.dependenceSurne.form.get('depSurneGreatDependency');
    if (!sevCtrl || !greatCtrl) { return; }
    const sev = toNumber(sevCtrl.value) ?? 0;
    const great = sev * 2;
    if (toNumber(greatCtrl.value) !== great) {
      greatCtrl.patchValue(great, { emitEvent: false });
    }
  }

  public initFromViewModel(vm: ProjectViewModel['calculatorDependence']): void {
    const model = this.sharedSrv.getModel();
    if (!model) { throw new Error('ProjectViewModel not available'); }
    this.project = model;

    (Object.entries(this.vmMap) as Array<[keyof typeof this.vmMap, CalculatorProductViewModel]>)
      .forEach(([key, child]) => {
        const src = vm[key];
        child.form.patchValue(src.form.getRawValue(), { emitEvent: false });
        child.bonus = src.bonus ?? 0;
        child.chargeable = !!src.chargeable;
        child.productName = src.productName ?? '';
        child.trace = src.trace ?? '';
        child.maximumCapital = src.maximumCapital ?? 0;
        child.summaryCapitals = src.summaryCapitals ?? '';
        child.messages = Array.isArray(src.messages) ? [...src.messages] : [];
      });

    const initialAxaSevere = this.dependenceAxa.form.get('depAxaSevereDependency')!.value;
    const initialSurneIncome = this.dependenceSurne.form.get('depSurneCapitalIncome')!.value;
    const initialSurneSevere = this.dependenceSurne.form.get('depSurneSevereDependency')!.value;

    const normalizedSurneIncome = toNumber(initialSurneIncome) ?? 0;

    this.uiBlocker.block();
    forkJoin([
      this.calcSrv.getSelectDepAxaSevereDependency(),
      this.calcSrv.getSelectDepSurneCapitalIncome(),
      this.calcSrv.getSelectSurneSevereDependency(normalizedSurneIncome)
    ])
      .pipe(finalize(() => {
        this.uiBlocker.unblock();
        this.cdr.markForCheck();
      }))
      .subscribe(([axaList, surneIncomeList, surneSevereList]) => {
        this.dependenceAxa.setMasterValues('S_DEP_AXA_SEVEREDEPENDENCY', axaList);
        this.dependenceAxa.form.get('depAxaSevereDependency')!
          .setValue(initialAxaSevere, { emitEvent: false });

        this.dependenceSurne.setMasterValues('S_DEP_SURNE_CAPITALINCOME', surneIncomeList);
        this.dependenceSurne.form.get('depSurneCapitalIncome')!
          .setValue(initialSurneIncome, { emitEvent: false });

        this.dependenceSurne.setMasterValues('S_DEP_SURNE_SEVEREDEPENDENCY', surneSevereList);
        this.dependenceSurne.form.get('depSurneSevereDependency')!
          .setValue(initialSurneSevere, { emitEvent: false });

        this.setAxaGreatFromSevere();
        this.setSurneGreatFromSevere();

        this.recalcAxa();
        this.recalcSurne();
      });

    this.setupListeners();
  }

  public flushToViewModel(): void {
    const vm = this.sharedSrv.getModel()!.calculatorDependence;
    (Object.entries(this.vmMap) as Array<[keyof typeof this.vmMap, CalculatorProductViewModel]>)
      .forEach(([key, child]) => {
        const tgt = vm[key];
        tgt.form.patchValue(child.form.getRawValue(), { emitEvent: false });
        tgt.bonus = child.bonus ?? 0;
        tgt.chargeable = !!child.chargeable;
        tgt.productName = child.productName ?? '';
        tgt.trace = child.trace ?? '';
        tgt.maximumCapital = child.maximumCapital ?? 0;
        tgt.summaryCapitals = child.summaryCapitals ?? '';
        tgt.messages = Array.isArray(child.messages) ? [...child.messages] : [];
      });
  }

  private setupListeners(): void {
    this.dependenceAxa.changes$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.setAxaGreatFromSevere();
        this.recalcAxa();
      });

    this.dependenceSurne.changes$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.setSurneGreatFromSevere();
        this.recalcSurne();
      });

    const incomeCtrl = this.dependenceSurne.form.get('depSurneCapitalIncome');
    if (incomeCtrl) {
      incomeCtrl.valueChanges
        .pipe(
          map(v => toNumber(v) ?? 0),
          distinctUntilChanged(),
          switchMap(typeDependency =>
            this.calcSrv.getSelectSurneSevereDependency(typeDependency)
          ),
          takeUntil(this.destroy$)
        )
        .subscribe((opts) => {
          const severeCtrl = this.dependenceSurne.form.get('depSurneSevereDependency');
          const previous = severeCtrl?.value;
          this.dependenceSurne.setMasterValues('S_DEP_SURNE_SEVEREDEPENDENCY', opts);
          if (severeCtrl) {
            const stillValid = Array.isArray(opts) && opts.some((o: any) => String(o?.id) === String(previous));
            severeCtrl.setValue(stillValid ? previous : (opts?.[0]?.id ?? null), { emitEvent: false });
          }
          this.setSurneGreatFromSevere();
          this.recalcSurne();
        });
    }
  }

  private recalc(obs$: Observable<unknown>): void {
    this.uiBlocker.block();
    obs$
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.uiBlocker.unblock();
          this.cdr.markForCheck();
        })
      )
      .subscribe();
  }

  private recalcAxa(): void {
    this.recalc(
      CalculatorMapper.calcDependencyAxa(
        this.project,
        this.dependenceAxa,
        req => this.calcSrv.resultBonusDepAxa(req)
      )
    );
  }

  private recalcSurne(): void {
    this.recalc(
      CalculatorMapper.calcDependencySurne(
        this.project,
        this.dependenceSurne,
        req => this.calcSrv.resultBonusDepSurne(req)
      )
    );
  }
}
